import {useQuery, UseQueryOptions} from '@tanstack/react-query';
import CustomerPortalAPI, {DeepThoughtError, Policy, PolicyStatus} from '../../../APIs/CustomerPortalAPI';

type UsePoliciesForCustomerQueryInput = {
    customerId?: string;
};

type UsePoliciesForCustomerOptions = {
    onQuery?: (input: UsePoliciesForCustomerQueryInput) => void;
} & Omit<UseQueryOptions<Policy[], Error, Policy[]>, 'queryKey' | 'queryFn' | 'enabled'>;

export function queryKeyFn(customerId: string | undefined) {
    return ['policies', {customerId}];
}

export function usePoliciesForCustomerQuery(
    {customerId}: UsePoliciesForCustomerQueryInput,
    options?: UsePoliciesForCustomerOptions,
) {
    return useQuery<Policy[], Error, Policy[]>({
        queryKey: queryKeyFn(customerId),
        queryFn: async () => {
            options?.onQuery?.({customerId});
            // We know that customerId exists because the enabled flag makes sure but cannot be inferred here
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const resp = await CustomerPortalAPI.getPoliciesForCustomer({customerId: customerId!});
            if (resp.errors || !resp.data?.policies) throw new DeepThoughtError(resp.errors);
            return resp.data.policies;
        },

        ...options,
        enabled: !!customerId,
        select: (data) =>
            data
                .filter((d) => d.status !== PolicyStatus.DELETED)
                .map((policy) => ({
                    ...policy,
                    requirements: policy.requirements.map((requirement) => ({
                        ...requirement,
                        dataJson: JSON.parse(requirement.dataJson ?? '{}'),
                    })),
                })),
    });
}

export default usePoliciesForCustomerQuery;
